import React, { useEffect, useId, useRef } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { List } from 'immutable';
import { Moment } from 'moment';
import moment from 'moment/moment';
import { useClose } from '@headlessui/react';

import { activateChannelProfile } from 'flow/actions/channels-actions';
import { activateExtensionProfile } from 'flow/actions/extension-actions';
import {
    activateQueueProfile,
    activateReferProfile,
} from 'flow/actions/pbx-actions';
import Divider from 'flow/components/ui-components/Divider';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import RouteLink from 'flow/components/ui-components/RouteLink';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import {
    selectExtensionActiveProfile,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { Profile } from 'flow/types/Profile';
import { routes } from 'flow/types/routes';

import { TypedMap } from 'flow/types/TypedMap';
import { ISO_8601_MS_RESOLUTION } from 'flow/utils';
import {
    getStandardProfileDescriptionInUserLanguage,
    resolveDefaultDuration,
} from 'flow/utils/profile-utils';
import { generatePath } from 'flow/utils/router-utils';
import { twMerge } from 'tailwind-merge';

export type ProfileToFocus = 'first' | 'last' | null;

interface Props {
    profiles: List<TypedMap<Profile>>;
    extensionKey: ExtensionKey;
    showSettingsLink: boolean;
    /** A profile to move focus to, once; then `onProfileToFocusHandled` is called. */
    profileToFocus?: ProfileToFocus;
    onProfileToFocusHandled?: () => void;
}

const Profiles = ({
    profiles,
    extensionKey,
    showSettingsLink,
    profileToFocus = null,
    onProfileToFocusHandled,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();
    // Closes the picker and returns focus to its button.
    const close = useClose();
    const idPrefix = useId();
    const profileButtons = useRef<Array<HTMLButtonElement | null>>([]);

    useEffect(() => {
        if (!profileToFocus) {
            return;
        }

        const index = profileToFocus === 'first' ? 0 : profiles.size - 1;
        profileButtons.current[index]?.focus();
        onProfileToFocusHandled?.();
    }, [profileToFocus, profiles.size, onProfileToFocusHandled]);

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const key = extensionKey || ownExtensionKey;

    const activeProfile = useAppSelector((state) =>
        selectExtensionActiveProfile(state, key),
    );

    const activate = (profile: TypedMap<Profile>, until: Moment) => {
        const [profileType]: string[] = key.split('-');

        let updatedProfileDTO = profile
            .update('active', () => true)
            .update('activeUntil', () =>
                until ? until.format(ISO_8601_MS_RESOLUTION) : null,
            );

        /** Flag every explicit picker selection, regardless of the
         * current (possibly stale) pause state. The flag means "the user chose
         * this profile"; the backend decides whether a pause needs cancelling.
         * Gating on a client-side "is paused" check would risk omitting the flag
         * during the exact client/server race this fix addresses. Only extension
         * profiles have pause semantics, so the flag is scoped to them.
         * **/
        if (profileType === 'extension') {
            updatedProfileDTO = updatedProfileDTO.set(
                'explicitSelection',
                true,
            );
        }

        const activators: Record<string, Function> = {
            queue: activateQueueProfile,
            refer: activateReferProfile,
            extension: activateExtensionProfile,
            channel: activateChannelProfile,
        };

        if (!activators[profileType]) {
            throw new Error('Not implemented');
        }

        dispatch(activators[profileType](key, updatedProfileDTO));
    };

    const pick = (profile: TypedMap<Profile>, until: Moment) => {
        activate(profile, until);
        close();
    };

    // ArrowUp and ArrowDown move between the profiles, Home and End to the
    // first and last. They skip the end-time buttons, which Tab still reaches,
    // and stop at the ends of the list.
    const moveBetweenProfiles = (
        event: React.KeyboardEvent<HTMLButtonElement>,
        index: number,
    ) => {
        if (event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }

        const last = profiles.size - 1;
        const targets: Record<string, number> = {
            ArrowUp: Math.max(index - 1, 0),
            ArrowDown: Math.min(index + 1, last),
            Home: 0,
            End: last,
        };
        const target = targets[event.key];

        if (target === undefined) {
            return;
        }

        // The keys would otherwise scroll the list.
        event.preventDefault();
        event.stopPropagation();
        profileButtons.current[target]?.focus();
    };

    const openEndTime = (profile: TypedMap<Profile>) => {
        openModal('setProfileUntil', {
            profile,
            onSubmit: activate,
        });
        close();
    };

    return (
        <>
            <ul className="scrollbar max-h-[340px] px-3 py-1">
                {profiles.map((profile, index) => {
                    const until = resolveDefaultDuration(
                        profile.get('defaultDuration'),
                        moment(),
                    );
                    const isActive =
                        profile.get('key') === activeProfile?.get('key');
                    const nameId = `${idPrefix}-${index}-name`;
                    const endTimeId = `${idPrefix}-${index}-end-time`;

                    return (
                        <li
                            key={profile.get('key')}
                            className="group relative flex items-center rounded-sm pe-1 select-none hover:bg-gray-200 has-[:focus-visible]:bg-gray-200 dark:hover:bg-gray-300 dark:has-[:focus-visible]:bg-gray-300"
                        >
                            {/* stretched-control: a click anywhere on the row
                                picks the profile. The end-time button is
                                positioned, so it keeps its own clicks. */}
                            <button
                                ref={(button) => {
                                    profileButtons.current[index] = button;
                                }}
                                type="button"
                                aria-current={isActive ? 'true' : undefined}
                                className="stretched-control flex min-w-0 flex-1 cursor-pointer items-center gap-x-3 text-start"
                                onClick={() => pick(profile, until)}
                                onKeyDown={(event) =>
                                    moveBetweenProfiles(event, index)
                                }
                            >
                                <span className="p-3 pe-0">
                                    <span
                                        className={twMerge(
                                            'block size-4 rounded-full',
                                            profile.get('available')
                                                ? 'bg-green'
                                                : 'bg-white ring-2 ring-gray-400 ring-inset',
                                        )}
                                    />
                                </span>

                                <span
                                    id={nameId}
                                    dir="auto"
                                    className={twMerge(
                                        'flex-1 truncate',
                                        isActive
                                            ? 'font-medium'
                                            : 'font-normal',
                                    )}
                                >
                                    {getStandardProfileDescriptionInUserLanguage(
                                        profile.get('description'),
                                        i18next,
                                    )}
                                </span>
                            </button>

                            {profile.get('plusId') !== 0 ? (
                                // Named "Set end time" plus the profile, so
                                // each row's button has a name of its own.
                                <IconButton
                                    type="button"
                                    id={endTimeId}
                                    aria-label={i18next.t(
                                        'profile.set_end_time',
                                    )}
                                    aria-labelledby={`${endTimeId} ${nameId}`}
                                    onClick={() => openEndTime(profile)}
                                    onKeyDown={(event) =>
                                        moveBetweenProfiles(event, index)
                                    }
                                    className="reveal-on-hover-or-focus relative ms-3"
                                >
                                    <Icon iconKey="accessTime" />
                                </IconButton>
                            ) : null}
                        </li>
                    );
                })}
            </ul>

            {showSettingsLink && (
                <>
                    <Divider className="my-0 dark:bg-gray-300" />
                    <div className="p-3">
                        <RouteLink
                            to={generatePath(routes['settings.profiles'], {
                                extensionKey: key,
                            })}
                            onClick={close}
                        >
                            {i18next.t('Settings.Profiles.Edit')}
                        </RouteLink>
                    </div>
                </>
            )}
        </>
    );
};

export default React.memo(Profiles);
